import { Link } from "react-router-dom";
import { BadgeCheck, Eye, Heart, MapPin, Truck } from "lucide-react";
import { mediaUrl, money } from "@/lib/api";
import { STATUS_LABELS, STATUS_TONES } from "@/lib/constants";

export const StatusPill = ({ status }) => (
  <span
    data-testid={`status-pill-${status}`}
    className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
      STATUS_TONES[status] || "bg-muted text-muted-foreground"
    }`}
  >
    {STATUS_LABELS[status] || status}
  </span>
);

export const ListingCard = ({ listing, onFavorite, isFavorite, showStatus }) => {
  const cover = listing.photos?.[listing.cover_index || 0] || listing.photos?.[0];
  const verified = listing.seller?.verifications
    ? Object.values(listing.seller.verifications).some(Boolean)
    : false;

  return (
    <article
      data-testid={`listing-card-${listing.id}`}
      className="group flex flex-col overflow-hidden rounded-lg border border-border bg-card transition-colors duration-200 hover:border-secondary"
    >
      <Link to={`/ilan/${listing.id}`} className="relative block aspect-[4/3] overflow-hidden bg-muted">
        {cover ? (
          <img
            src={mediaUrl(cover)}
            alt={listing.title}
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Fotoğraf yok
          </div>
        )}
        <div className="absolute left-3 top-3 flex flex-wrap gap-2">
          {listing.featured && (
            <span className="rounded-full bg-accent px-2.5 py-1 text-xs font-bold text-accent-foreground">
              Öne Çıkan
            </span>
          )}
          <span className="rounded-full bg-primary px-2.5 py-1 text-xs font-semibold text-primary-foreground">
            {listing.listing_type}
          </span>
          {listing.is_demo && (
            <span
              data-testid={`demo-badge-${listing.id}`}
              className="rounded-full bg-accent px-2.5 py-1 text-xs font-bold text-accent-foreground"
            >
              Örnek İlan
            </span>
          )}
        </div>
        {onFavorite && (
          <button
            type="button"
            data-testid={`favorite-btn-${listing.id}`}
            onClick={(e) => {
              e.preventDefault();
              onFavorite(listing);
            }}
            className="absolute right-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-background/95 text-primary transition-transform duration-150 active:scale-90"
            aria-label="Favorilere ekle"
          >
            <Heart className={`h-5 w-5 ${isFavorite ? "fill-destructive text-destructive" : ""}`} />
          </button>
        )}
      </Link>

      <div className="flex flex-1 flex-col gap-3 p-4">
        {showStatus && <StatusPill status={listing.status} />}
        <Link to={`/ilan/${listing.id}`} className="font-display text-base font-bold leading-snug text-primary">
          {listing.title}
        </Link>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-4 w-4" /> {listing.province}
            {listing.district ? ` / ${listing.district}` : ""}
          </span>
          {listing.transport_available && (
            <span className="inline-flex items-center gap-1">
              <Truck className="h-4 w-4" /> Nakliye
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            <Eye className="h-4 w-4" /> {listing.views ?? 0}
          </span>
        </div>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div>
            <p className="font-display text-lg font-extrabold text-primary">
              {money(listing.price, listing.currency)}
            </p>
            {listing.negotiable && <p className="text-xs text-muted-foreground">Pazarlığa açık</p>}
          </div>
          {verified && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary">
              <BadgeCheck className="h-4 w-4 text-accent" /> Doğrulanmış
            </span>
          )}
        </div>
      </div>
    </article>
  );
};

export const ListingCardSkeleton = () => (
  <div className="animate-pulse overflow-hidden rounded-lg border border-border bg-card">
    <div className="aspect-[4/3] bg-muted" />
    <div className="space-y-3 p-4">
      <div className="h-4 w-3/4 rounded bg-muted" />
      <div className="h-3 w-1/2 rounded bg-muted" />
      <div className="h-5 w-1/3 rounded bg-muted" />
    </div>
  </div>
);
